import { Button, Card, Tab } from '@alifd/next';
import { DatePicker, Input, message, Select, Space, Table } from 'antd';
import React, { useState } from 'react';
import useSWR, { useSWRConfig } from 'swr';

import LoadingSpin from '@/components/UI/LoadingSpin';

import AddReservation from '../AddReservation';
import ExportExcel from '../ExportExcel';
import sharedStyles from '../sharedStyles.module.scss';
import {
  bookingListAPIUrl,
  BookingListTypeConst,
  reservationRulesAPIUrl,
} from './constants';
import {
  fetchBookingList,
  fetchReservationRules,
  getBookingListColumns,
  getLatestBookingList,
  showTableBookingList,
} from './helper';
import styles from './index.module.scss';
import type { RangeValue } from './types';

const { RangePicker } = DatePicker;

const ReservationList = () => {
  const { mutate } = useSWRConfig();

  const [bookingListType, setBookingListType] = useState<
    typeof BookingListTypeConst.Reserve | typeof BookingListTypeConst.History
  >(BookingListTypeConst.Reserve);
  const [showAddFormModal, setShowAddFormModal] = useState(false);
  const [phoneNumberInput, setPhoneNumberInput] = useState('');
  const [communityName, setCommunityName] = useState('');
  const [cleanKindName, setCleanKindName] = useState('');
  const [rangeOfDate, setRangeOfDate] = useState<RangeValue>(null);

  const {
    data: cleanBookingList,
    error: fetchBookingListError,
    isLoading: isBookingListFetcherLoading,
  } = useSWR(
    bookingListAPIUrl,
    (url) => {
      try {
        const data = fetchBookingList(url, bookingListType);
        return data;
      } catch (error) {
        return undefined;
      }
    },
    {
      revalidateOnFocus: false,
      shouldRetryOnError: false,
    }
  );
  const {
    data: reservationRules,
    isLoading: isReservationRulesFetcherLoading,
  } = useSWR(
    reservationRulesAPIUrl,
    (url) => {
      try {
        const data = fetchReservationRules(url);
        return data;
      } catch (error) {
        return undefined;
      }
    },
    {
      revalidateOnFocus: false,
      shouldRetryOnError: false,
    }
  );

  if (isBookingListFetcherLoading || isReservationRulesFetcherLoading) {
    return <LoadingSpin />;
  }

  if (fetchBookingListError) {
    message.error('預約清單載入失敗!');
  }

  const hideAddFormModal = () => {
    setShowAddFormModal(false);
  };

  const handleTabChange = (
    key:
      | typeof BookingListTypeConst.Reserve
      | typeof BookingListTypeConst.History
  ) => {
    mutate(bookingListAPIUrl, fetchBookingList(bookingListAPIUrl, key));
    setBookingListType(key);
  };

  const communityNameOptionsForFilter = cleanBookingList?.reduce(
    (options: { label: string; value: string }[], reservation) => {
      const isOptionExist = options.some(
        (option) => option.value === reservation.communityName
      );
      if (isOptionExist === false) {
        options.push({
          label: reservation.communityName,
          value: reservation.communityName,
        });
      }
      return options;
    },
    []
  );
  const cleanKindNameOptionsForFilter = cleanBookingList?.reduce(
    (options: { label: string; value: string }[], reservation) => {
      const isOptionExist = options.some(
        (option) => option.value === reservation.cleanKindName
      );
      if (isOptionExist === false) {
        options.push({
          label: reservation.cleanKindName,
          value: reservation.cleanKindName,
        });
      }
      return options;
    },
    []
  );
  const tableBookingListColumns = getBookingListColumns(
    reservationRules?.mustCancelBefore,
    bookingListType
  );
  const tableBookingList = showTableBookingList({
    cleanBookingList,
    phoneNumberInput,
    communityName,
    cleanKindName,
    rangeOfDate,
  });

  return (
    <Space direction="vertical" className={sharedStyles.widthFull}>
      <Space>
        <Button
          onClick={() => {
            setShowAddFormModal(true);
          }}
        >
          新增預約
        </Button>
        <ExportExcel />
      </Space>

      <Space align="baseline" size="middle">
        <div className={styles.filterContainer}>
          <span>會員：</span>
          <Input
            placeholder="例如：(02)6633-6633"
            className={styles.filterWidth}
            allowClear
            onChange={(event) => setPhoneNumberInput(event.target.value)}
          />
        </div>
        <div>
          <span>社區：</span>
          <Select
            placeholder="請選擇社區"
            className={styles.filterWidth}
            options={communityNameOptionsForFilter}
            allowClear
            onChange={setCommunityName}
          />
        </div>
        <div>
          <span>類別：</span>
          <Select
            placeholder="請選擇清潔類別"
            className={styles.filterWidth}
            options={cleanKindNameOptionsForFilter}
            allowClear
            onChange={setCleanKindName}
          />
        </div>
        <div>
          <span>時間：</span>
          <RangePicker inputReadOnly onChange={setRangeOfDate} />
        </div>
      </Space>

      <Tab onChange={handleTabChange} className={styles.tabMarginBottom}>
        <Tab.Item title="預約清單" key={BookingListTypeConst.Reserve} />
        <Tab.Item title="歷史紀錄" key={BookingListTypeConst.History} />
      </Tab>
      <Card free>
        <Card.Content>
          <Table
            bordered
            rowKey="cleanBookingsId"
            scroll={{ x: 'max-content' }}
            columns={tableBookingListColumns}
            dataSource={tableBookingList}
          />
        </Card.Content>
      </Card>
      <AddReservation
        isOpenMainModal={showAddFormModal}
        hideAddFormModal={hideAddFormModal}
        getLatestBookingList={() => getLatestBookingList(bookingListType)}
      />
    </Space>
  );
};

export default ReservationList;
